/**
 * Playout on the wire (PLAN §5.2, §5.3, §6.6): operator commands, the playout
 * state a UI renders, and the as-run log.
 *
 * **Commands (§6.6: "REST command → the active production. Explicit
 * ack/nack. Never merged").** One route, `POST /productions/{id}/commands`,
 * whose body is a union discriminated on `type` — §5.3's v1 operations plus
 * D19's `syncToInstance`. Every command carries a client-minted `commandId`:
 * the production answers a repeated id with the answer it gave the first
 * time and does not run it twice, so a client may retry a command whose
 * answer it lost. The answer is an ack (`accepted: true`, the playout `seq`
 * that includes the command's effect) or a nack (`accepted: false`, a
 * machine-readable `reason` and a message). A nack is a 200: the production
 * heard the command and said no. Failing to REACH the production is an error
 * (`unavailable`), because then nobody said anything.
 *
 * **Playout state.** Owned by the active production process alone (§6.6),
 * sequence-numbered: `seq` increases with every change. The shape is a
 * projection of the engine's (`@acp/engine/state.js`'s `EngineState` and
 * `timings.ts`'s `getTimings()`): the part and piece instances of §5.2
 * (`@acp/domain/instances.js` — the engine's instances without its private
 * bookkeeping), the activation, `takeCount`/`lastTakeTime`/`startedPlayback`/
 * `autoNext`/`history`, the timings of §7.4 and the device statuses of §7.2 —
 * so mapping the engine's state onto it is a copy, not an interpretation. `schemaVersion` is bumped only on a breaking change
 * (never, while pinned productions may speak the old one, §4.3/§6.8);
 * additions are additive.
 *
 * **As-run (§5.4, §6.6).** Append-only events with ULID ids, read with a
 * cursor (the last id seen). The production writes them at least once and
 * idempotently (a repeated id is kept once).
 */
import { partInstanceSchema, pieceInstanceSchema } from '@acp/domain/instances.js';
import { epochMsSchema, fieldPathSchema, jsonValueSchema, opaqueIdSchema, ulidSchema } from '@acp/domain/primitives.js';
import { z } from 'zod';
import type { PermissionId } from './permissions.js';

// ─── commands ───

/** A client-minted command id: the idempotency key of a command (§6.6). */
export const commandIdSchema = opaqueIdSchema;

/** A config-level id: a piece type, an action, a layer, a device (the studio config's keys, D22). */
export const configIdSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_.:-]{0,127}$/, 'config ids are 1-128 of [A-Za-z0-9_.:-]');

/** The most parts `moveNext` moves in one command: beyond this, name the part (`setNext`). */
export const MAX_MOVE_NEXT = 100;

function command<T extends string, S extends z.ZodRawShape>(type: T, shape: S) {
  return z.object({ type: z.literal(type), commandId: commandIdSchema, ...shape }).strict();
}

export const activateCommandSchema = command('activate', {
  /** Rehearsal mode (Sofie's): playout runs, nothing airs for real. Null: the production's own `rehearsal`. */
  rehearsal: z.boolean().nullable(),
});
export const deactivateCommandSchema = command('deactivate', {});
/** Back to the top: a fresh activation with no played instances (§5.3). */
export const resetCommandSchema = command('reset', {});
/** Next becomes current. Guarded by `minimumTakeSpan` (§5.3). */
export const takeCommandSchema = command('take', {});
export const setNextCommandSchema = command('setNext', { partId: ulidSchema });
export const moveNextCommandSchema = command('moveNext', {
  /** Parts forward (+) or back (−) from the current next. */
  delta: z
    .number()
    .int()
    .min(-MAX_MOVE_NEXT)
    .max(MAX_MOVE_NEXT)
    .refine((d) => d !== 0, { message: 'delta must not be 0' }),
});
/** Next becomes the first part of this segment, now. */
export const setNextSegmentCommandSchema = command('setNextSegment', { segmentId: ulidSchema });
/** When the current segment ends, continue with this one (instead of the following segment). */
export const queueNextSegmentCommandSchema = command('queueNextSegment', { segmentId: ulidSchema });
export const adlibModeSchema = z.enum(['now', 'queue']);
/**
 * Play a piece of `pieceType` with `params` (validated against the piece
 * type's param schema by the production): `now` into the current part,
 * `queue` as an adlib part inserted as next (§5.1).
 */
export const adlibCommandSchema = command('adlib', {
  pieceType: configIdSchema,
  params: z.record(z.string(), jsonValueSchema),
  mode: adlibModeSchema,
});
export const stopPiecesOnLayerCommandSchema = command('stopPiecesOnLayer', { layer: configIdSchema });
/** Skip the next piece due to start in the current part (§5.3). */
export const disableNextPieceCommandSchema = command('disableNextPiece', {});
/** Run a macro action of the studio config (`actions`, D22). */
export const executeActionCommandSchema = command('executeAction', { actionId: configIdSchema });
/**
 * Sync the named live-editable fields of the current part's definition into
 * its running instance (D19: "Update on air?"). Needs `playout.take`.
 */
export const syncToInstanceCommandSchema = command('syncToInstance', {
  partInstanceId: ulidSchema,
  fields: z.array(fieldPathSchema).min(1).max(50),
});

export const playoutCommandSchema = z.discriminatedUnion('type', [
  activateCommandSchema,
  deactivateCommandSchema,
  resetCommandSchema,
  takeCommandSchema,
  setNextCommandSchema,
  moveNextCommandSchema,
  setNextSegmentCommandSchema,
  queueNextSegmentCommandSchema,
  adlibCommandSchema,
  stopPiecesOnLayerCommandSchema,
  disableNextPieceCommandSchema,
  executeActionCommandSchema,
  syncToInstanceCommandSchema,
]);
export type PlayoutCommand = z.infer<typeof playoutCommandSchema>;
export type PlayoutCommandType = PlayoutCommand['type'];

/**
 * The permission each command needs on the production's studio (D14, D19):
 * `playout.activate` for the activation lifecycle, `playout.take` for
 * everything an operator does on air.
 */
export const COMMAND_PERMISSIONS: Readonly<Record<PlayoutCommandType, PermissionId>> = {
  activate: 'playout.activate',
  deactivate: 'playout.activate',
  reset: 'playout.activate',
  take: 'playout.take',
  setNext: 'playout.take',
  moveNext: 'playout.take',
  setNextSegment: 'playout.take',
  queueNextSegment: 'playout.take',
  adlib: 'playout.take',
  stopPiecesOnLayer: 'playout.take',
  disableNextPiece: 'playout.take',
  executeAction: 'playout.take',
  syncToInstance: 'playout.take',
};

/** The permissions `POST /productions/{id}/commands` can require, for its gate and docs. */
export const PLAYOUT_COMMAND_PERMISSIONS: readonly PermissionId[] = ['playout.take', 'playout.activate'];

/**
 * Why a production refused a command. Open (a production of a newer version
 * may give a reason this build does not know, §4.3), with these known values:
 *
 *   production-ended   the production has ended; it takes no more commands
 *   not-active         the playlist is not activated (take, next, adlib…)
 *   already-active     activate while active
 *   no-next            take with nothing set as next
 *   take-too-soon      within `minimumTakeSpan` of the previous take (§5.3)
 *   unknown-part       setNext/moveNext/segment to a part or segment that is not in the playlist (or is invalid)
 *   unknown-piece-type adlib of a piece type the config snapshot lacks
 *   unknown-action     executeAction of an action the config snapshot lacks
 *   invalid-params     the adlib's params fail the piece type's schema
 *   unknown-instance   syncToInstance of an instance that is not current or next
 *   not-live-editable  syncToInstance of a field that is not live-editable (D19)
 *   nothing-to-disable disableNextPiece with no piece left to start
 */
export const nackReasonSchema = z.string().regex(/^[a-z][a-z0-9-]{0,63}$/);

export const commandResultSchema = z.discriminatedUnion('accepted', [
  z
    .object({
      commandId: commandIdSchema,
      accepted: z.literal(true),
      /** The playout `seq` whose state includes this command's effect. */
      seq: z.number().int().nonnegative(),
    })
    .strict(),
  z
    .object({
      commandId: commandIdSchema,
      accepted: z.literal(false),
      reason: nackReasonSchema,
      message: z.string().max(1000),
    })
    .strict(),
]);
export type CommandResult = z.infer<typeof commandResultSchema>;

// ─── playout state ───

/** The current wire version of `PlayoutState`. */
export const PLAYOUT_STATE_SCHEMA_VERSION = 1;

/** A part instance with its piece instances (§5.2): the engine's own shapes. */
export const partInstanceStateSchema = z
  .object({
    instance: partInstanceSchema,
    pieces: z.array(pieceInstanceSchema),
  })
  .strict();
export type PartInstanceState = z.infer<typeof partInstanceStateSchema>;

export const activationSchema = z
  .object({
    status: z.enum(['inactive', 'active', 'rehearsal']),
    /** The activation the instances belong to (a reset starts a new one); null while inactive. */
    activationId: ulidSchema.nullable(),
    activatedAt: epochMsSchema.nullable(),
  })
  .strict();

const signedMsSchema = z.number().int();

/** The on-air part's clock (the engine's `OnAirTimings`). */
export const onAirTimingSchema = z
  .object({
    partInstanceId: ulidSchema,
    partId: ulidSchema,
    startedAt: epochMsSchema,
    elapsedMs: z.number().int().nonnegative(),
    expectedDurationMs: z.number().int().nonnegative().nullable(),
    /** Countdown to the expected end; negative once over; null without an expected duration. */
    remainingMs: signedMsSchema.nullable(),
    /** When an auto-next hands over, if it will. */
    autoNextAt: epochMsSchema.nullable(),
  })
  .strict();

/** A piece's countdown (the engine's `PieceCountdown`). */
export const pieceCountdownSchema = z
  .object({
    pieceInstanceId: ulidSchema,
    sourceLayer: z.string().max(128),
    startsAt: epochMsSchema,
    endsAt: epochMsSchema.nullable(),
    /** Until it starts; ≤ 0 once it has. */
    startsInMs: signedMsSchema,
    /** Until it ends; null when open-ended. */
    remainingMs: signedMsSchema.nullable(),
  })
  .strict();

/** A segment's budget and pace (the engine's `SegmentTimings`). */
export const segmentTimingSchema = z
  .object({
    segmentId: ulidSchema,
    budgetMs: z.number().int().nonnegative().nullable(),
    expectedMs: z.number().int().nonnegative(),
    playedMs: z.number().int().nonnegative(),
    remainingMs: signedMsSchema,
    overUnderMs: signedMsSchema,
  })
  .strict();

/**
 * Timings (§7.4) as the engine computes them at `now` (its `PlaylistTimings`,
 * `getTimings()`): a UI counts down locally from these and re-bases on the
 * next state.
 */
export const playoutTimingSchema = z
  .object({
    /** The production's clock when this state was produced. */
    now: epochMsSchema,
    /** The playlist's timing mode (PLAN §5.1); `none` for a rundown run alone. */
    mode: z.enum(['none', 'forward-time', 'back-time', 'duration']),
    /** The planned length of every timed part. */
    totalExpectedMs: z.number().int().nonnegative(),
    playedMs: z.number().int().nonnegative(),
    remainingMs: signedMsSchema,
    /** The end the playlist's timing asks for; null when it names none. */
    expectedEnd: epochMsSchema.nullable(),
    /** When it will end at the current pace; null before it starts. */
    projectedEnd: epochMsSchema.nullable(),
    /** Over (+) / under (−) against the timing, ms; null when there is nothing to hold it to. */
    overUnderMs: signedMsSchema.nullable(),
    onAir: onAirTimingSchema.nullable(),
    pieces: z.array(pieceCountdownSchema),
    segments: z.array(segmentTimingSchema),
  })
  .strict();

/** One part as played in this activation (the engine's `PlayedPart`): the as-played record the timings count. */
export const playedPartSchema = z
  .object({
    partInstanceId: ulidSchema,
    partId: ulidSchema,
    segmentId: ulidSchema,
    takeCount: z.number().int().nonnegative(),
    untimed: z.boolean(),
    startedAt: epochMsSchema,
    stoppedAt: epochMsSchema,
  })
  .strict();

/** One device's status (§7.2): connectivity and health as the production sees it. */
export const deviceStatusSchema = z
  .object({
    deviceId: configIdSchema,
    /** The driver type from the config (`vmix`, `casparcg`, `sim-video-server`, …). */
    type: z.string().min(1).max(64),
    connected: z.boolean(),
    status: z.enum(['good', 'warning', 'bad', 'unknown']),
    /** Human-readable detail; null when there is nothing to say. */
    message: z.string().max(1000).nullable(),
    /** When the production last heard from it; null if never. */
    lastSeen: epochMsSchema.nullable(),
  })
  .strict();
export type DeviceStatus = z.infer<typeof deviceStatusSchema>;

export const playoutStateSchema = z
  .object({
    schemaVersion: z.literal(PLAYOUT_STATE_SCHEMA_VERSION),
    productionId: ulidSchema,
    /** Increases with every change of this state (§6.6's sequence-numbered stream). */
    seq: z.number().int().nonnegative(),
    activation: activationSchema,
    previous: partInstanceStateSchema.nullable(),
    current: partInstanceStateSchema.nullable(),
    next: partInstanceStateSchema.nullable(),
    /** Whether next was set by an operator (setNext, moveNext…) rather than by the running order. */
    nextSetManually: z.boolean(),
    /** The segment queued by `queueNextSegment`, if any. */
    queuedSegmentId: ulidSchema.nullable(),
    /** Takes in this activation (the on-air instance's `takeCount`). */
    takeCount: z.number().int().nonnegative(),
    lastTakeTime: epochMsSchema.nullable(),
    /** The first timed take of this activation (Sofie's `startedPlayback`): where over/under counts from. */
    startedPlayback: epochMsSchema.nullable(),
    /** A pending automatic take (the engine's `autoNext`): when, and from which on-air instance. */
    autoNext: z.object({ at: epochMsSchema, fromPartInstanceId: ulidSchema }).strict().nullable(),
    /** The as-played record of this activation, oldest first. */
    history: z.array(playedPartSchema),
    timing: playoutTimingSchema,
    devices: z.array(deviceStatusSchema),
  })
  .strict();
export type PlayoutState = z.infer<typeof playoutStateSchema>;

export const playoutStateResponseSchema = z.object({ state: playoutStateSchema }).strict();

/** The members of a playout state a delta may carry: everything but its envelope (`schemaVersion`, `productionId`, `seq`). */
export const playoutStateMembersSchema = playoutStateSchema.omit({ schemaVersion: true, productionId: true, seq: true });
export type PlayoutStateMembers = z.infer<typeof playoutStateMembersSchema>;
/** A delta's `changes`: the members that changed, each whole. */
export type PlayoutStateChanges = Partial<PlayoutStateMembers>;

/** Every member name, from the schema itself: a member added to the state is in every delta that changes it. */
export const PLAYOUT_STATE_MEMBERS = playoutStateMembersSchema.keyof().options;

/**
 * The members of `after` that differ from `before`, each whole — what a
 * `delta` from `before.seq` to `after.seq` carries, so that applying it to
 * `before` gives `after` (one definition for every producer of deltas).
 */
export function changedPlayoutMembers(before: PlayoutState, after: PlayoutState): PlayoutStateChanges {
  const changes: PlayoutStateChanges = {};
  for (const key of PLAYOUT_STATE_MEMBERS) {
    if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) Object.assign(changes, { [key]: after[key] });
  }
  return changes;
}

/**
 * A change to the playout state on the WS stream (`playout.state`): the whole
 * state, or the top-level members that changed since `fromSeq`. A client
 * applies a delta only on top of exactly `fromSeq`; any other gap means it
 * missed a frame, and it re-reads `GET /productions/{id}/playout`.
 */
export const playoutStateChangeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('full'), state: playoutStateSchema }).strict(),
  z
    .object({
      kind: z.literal('delta'),
      fromSeq: z.number().int().nonnegative(),
      seq: z.number().int().positive(),
      /** The members that changed, each whole (a shallow merge over the state at `fromSeq`). */
      changes: playoutStateMembersSchema.partial().strict(),
    })
    .strict(),
]);
export type PlayoutStateChange = z.infer<typeof playoutStateChangeSchema>;

// ─── as-run ───

/**
 * Known as-run event types (open, as nack reasons): `production.created`,
 * `production.ended`, `command` (an operator command the production accepted,
 * with who sent it), `activated`, `deactivated`, `reset`, `take`,
 * `part.started`, `part.ended`, `piece.started`, `piece.stopped`.
 */
export const asRunTypeSchema = z.string().regex(/^[a-z][a-z0-9.-]{0,63}$/);

export const asRunEventSchema = z
  .object({
    /** A ULID: unique, and the log's order. */
    eventId: ulidSchema,
    type: asRunTypeSchema,
    /** When it happened (epoch ms, the writer's clock). */
    at: epochMsSchema,
    /** Who caused it, for operator actions; null for the engine's own events. */
    userId: z.string().min(1).max(128).nullable(),
    /** The command it records (`command` events). */
    commandId: commandIdSchema.nullable(),
    partInstanceId: ulidSchema.nullable(),
    pieceInstanceId: ulidSchema.nullable(),
    /** Type-specific detail. */
    detail: z.record(z.string(), jsonValueSchema),
  })
  .strict();
export type AsRunEvent = z.infer<typeof asRunEventSchema>;

export const asRunQuerySchema = z
  .object({
    cursor: ulidSchema.optional().describe('Only events after this one: the previous page’s `cursor`. Absent: from the start.'),
    limit: z.coerce.number().int().min(1).max(1000).optional().describe('Events per page, 1–1000; default 500.'),
  })
  .strict();

export const asRunResponseSchema = z
  .object({
    /** Oldest first. */
    events: z.array(asRunEventSchema),
    /** Pass as `cursor` to continue; null when the log is empty and no cursor was given. */
    cursor: ulidSchema.nullable(),
    /** Whether more events were already there beyond this page. */
    more: z.boolean(),
  })
  .strict();
